import { identifyUser, removeIdentity } from "utils/hubspot";
import { fetchCsrfToken } from "utils/csrf";

import { UserBuddyStatus } from "pages/employee/types";

import { Organization, RegisterAccountPayload, User } from "../types";
import { apiClient } from "../../utils/apiClient";
import { StorageKeys } from "constants/storage-keys";

const localStorageTokenKey = "__auth-token";
const localStorageDataKey = "__auth-data";
const localStorageRoomIdKey = "__auth-roomId";
const localStorageRoomTemplateIdKey = "__auth-roomTemplateId";
export const localStorageOrgSlugKey = "__auth-orgSlug";
const localStorageGameSlugKey = "__auth-gameSlug";
const localStorageUserDashboardKey = "__auth-userDashboard";
const localStorageOrgStripeEnabled = "__auth-orgOrderDeprecated";
export const localStorageShowConfirmNotifyBuddy = "__userDashboard-showConfirmNotifyBuddy";

const userLocaleStorageItems: string[] = [
	localStorageTokenKey,
	localStorageDataKey,
	localStorageRoomIdKey,
	localStorageRoomTemplateIdKey,
	localStorageOrgSlugKey,
	localStorageGameSlugKey,
	localStorageUserDashboardKey,
	localStorageOrgStripeEnabled,
];

const localStorageAdminTokenKey = "@ad__auth-token";
const localStorageAdminDataKey = "@ad__auth-data";

type LoginResponse = {
	data: {
		user: User;
		token: string;
		room: {
			roomId: string;
			roomTemplateId: string;
			organization: {
				slug: string;
			};
		};
		organizationSlug: string;
		organization: {
			slug: string;
		};
		gameSlug: string;
	};
};

type AdminLoginResponse = {
	data: {
		user: User;
		token: string;
		organization: {
			slug: string;
			isStripeEnabled: boolean;
		};
		accessProfile?: {
			preset: "full_access" | "view_only" | "custom";
			featureKeys: string[];
		};
	};
};

type AdminRegisterResponse = {
	data: {
		user: User;
		organization: {
			slug: string;
			isStripeEnabled: boolean;
		};
		token: string;
		accessProfile?: {
			preset: "full_access" | "view_only" | "custom";
			featureKeys: string[];
		};
	};
};

type AdminUserResponse = {
	data: {
		user: User;
		organization: Organization;
		accessProfile?: {
			preset: "full_access" | "view_only" | "custom";
			featureKeys: string[];
		};
	};
};

export async function login(data: { email: string; password: string }) {
	const response = await apiClient("auth/login", {
		data,
	});
	if (!response) return;
	if ((response.data as any)?.room?.users) return handleLoginResponseForGameRoom(response as LoginResponse);
	return handleLoginResponseForUserDashboard(response as LoginResponse);
}

export async function passwordlessLogin(data: { roomTemplateId: string; token: string }) {
	const response = await apiClient("auth/login/passwordless", {
		data,
	});
	if (!response) return;
	if ((response.data as any)?.room?.users) return handleLoginResponseForGameRoom(response as LoginResponse);
	return handleLoginResponseForUserDashboard(response as LoginResponse);
}

export async function verifyMagicLink(token: string) {
	const response = await apiClient("auth/magic-link/verify", {
		data: { token },
	});
	if (!response) return;
	if ((response.data as any)?.room?.users) return handleLoginResponseForGameRoom(response as LoginResponse);
	return handleLoginResponseForUserDashboard(response as LoginResponse);
}

export async function verifyAdminEmail(token: string): Promise<{ user?: { isEmailVerified: boolean } }> {
	const response = await apiClient(`auth/verify-email`, {
		data: { token },
	});
	return response?.data as { user?: { isEmailVerified: boolean } };
}

export async function fetchRoom(data: { roomId: string }) {
	const response = await apiClient(`rooms/${data.roomId}`, { token: getToken() });
	if (!response) return;
	return handleLoginRoomSetting(response as LoginResponse);
}

export const adminLogin = async (data: { username: string; password: string }) => {
	const response = await apiClient("auth/admin/login", {
		data,
	});
	if (!response) return;
	return handleAdminLoginResponse(response as AdminLoginResponse);
};

export const adminRegister = async (data: RegisterAccountPayload) => {
	const response = await apiClient("auth/admin/register", {
		data,
	});
	if (!response) return;
	return handleAdminRegisterResponse(response as AdminRegisterResponse);
};

export const resendToken = async () => {
	const token = getAdminToken();
	if (token) {
		await apiClient("auth/verify-email/resend", { token, data: {} });
	}
};

const handleActivateResponse = async (response: any) => {
	await fetchAccount();
	window.location.href = `/admin/onboarding/user`;
};

export const activate = async (data: { code: string; email: string }) => {
	const token = getAdminToken();
	const response = await apiClient("auth/otp-verification-email", { token, data });
	return handleActivateResponse(response);
};

export const resendCode = async (data: { email: string }) => {
	const token = getAdminToken();
	if (token) {
		await apiClient("auth/otp-verification-email/resend", { token, data });
	}
};

export const verifyAdminToken = async (): Promise<{ message?: string } | null> => {
	const token = getAdminToken();
	if (token) {
		const response = await apiClient(`auth/token-validity`, { token });
		return response?.data as { message?: string };
	}
	return null;
};

export const fetchAccount = async () => {
	if (window.localStorage.getItem(localStorageAdminTokenKey)) {
		const response = await apiClient("auth/admin/info", { token: getAdminToken() });
		if (!response) throw Error("Invalid response");
		return handleGetUserInfoResponse(response as AdminUserResponse);
	}

	throw Error("Invalid token");
};

export const verifyToken = async () => {
	if (window.localStorage.getItem(localStorageTokenKey)) {
		await apiClient(`auth/token-validity`, { token: getToken() });
	}
};

const handleAdminLoginResponse = async ({ data }: AdminLoginResponse) => {
	const userWithAccessProfile = {
		...data.user,
		accessProfile: data.accessProfile
	};

	window.localStorage.setItem(localStorageAdminTokenKey, data.token);
	window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(userWithAccessProfile));
	window.localStorage.setItem(localStorageOrgStripeEnabled, String(data.organization.isStripeEnabled));
	identifyUser({ email: data.user.email });
	await fetchCsrfToken();
	if (!data.user.isEmailVerified) {
		window.location.href = `/admin/activate`;
		window.localStorage.setItem(StorageKeys.ADMIN_PENDING_VERIFICATION, "true");
	} else {
		// Clear the pending verification flag on successful login
		window.localStorage.removeItem(StorageKeys.ADMIN_PENDING_VERIFICATION);
		window.location.href = `/admin/dashboard`;
	}
	return {
		...data,
		user: userWithAccessProfile
	};
};

const handleAdminRegisterResponse = async ({ data }: AdminRegisterResponse) => {
	const userWithAccessProfile = {
		...data.user,
		accessProfile: data.accessProfile
	};

	window.localStorage.setItem(localStorageAdminTokenKey, data.token);
	window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(userWithAccessProfile));
	window.localStorage.setItem(localStorageOrgStripeEnabled, String(data.organization.isStripeEnabled));
	identifyUser({ email: data.user.email });
	await fetchCsrfToken();
	if (!data.user.isEmailVerified) {
		window.location.href = `/admin/activate`;
		window.localStorage.setItem(StorageKeys.ADMIN_PENDING_VERIFICATION, "true");
	}
	return {
		...data,
		user: userWithAccessProfile
	};
};

const handleGetUserInfoResponse = ({ data }: AdminUserResponse) => {
	if (data.user.isEmailVerified) {
		const userWithAccessProfile = {
			...data.user,
			accessProfile: data.accessProfile
		};
		window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(userWithAccessProfile));
	}
	
	return {
		...data,
		user: {
			...data.user,
			accessProfile: data.accessProfile
		}
	};
};

async function handleLoginResponseForGameRoom({ data }: LoginResponse) {
	window.localStorage.setItem(localStorageTokenKey, data.token);
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(data.user));
	if (data.room?.roomId) {
		window.localStorage.setItem(localStorageRoomIdKey, data.room?.roomId);
	}
	if (data.room?.roomTemplateId) {
		window.localStorage.setItem(localStorageRoomTemplateIdKey, data.room?.roomTemplateId);
	}
	if (data.room?.organization.slug || data.organizationSlug) {
		window.localStorage.setItem(localStorageOrgSlugKey, data.room?.organization.slug || data.organizationSlug);
	}
	if (data.gameSlug) {
		window.localStorage.setItem(localStorageGameSlugKey, data.gameSlug);
	}
	await fetchCsrfToken();
	return data;
}
async function handleLoginResponseForUserDashboard({ data }: LoginResponse) {
	window.localStorage.setItem(localStorageTokenKey, data.token);
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(data.user));
	if (data.room?.[0]?.organization?.slug || data.organizationSlug) {
		window.localStorage.setItem(
			localStorageOrgSlugKey,
			data.room?.[0]?.organization?.slug || data.organizationSlug,
		);
	}
	if (data.gameSlug) {
		window.localStorage.setItem(localStorageGameSlugKey, data.gameSlug);
	}
	window.localStorage.setItem(localStorageUserDashboardKey, "user-dashboard");
	window.localStorage.setItem(localStorageShowConfirmNotifyBuddy, "show");
	await fetchCsrfToken();
	return data;
}

function handleLoginRoomSetting({ data }: LoginResponse) {
	window.localStorage.setItem(localStorageRoomIdKey, data.room?.roomId);
	window.localStorage.setItem(localStorageRoomTemplateIdKey, data.room?.roomTemplateId);
	return data;
}

export function userLogout() {
	for (let item of userLocaleStorageItems) {
		window.localStorage.removeItem(item);
	}
	removeIdentity(); // Remove hubspotutk cookie
}

export function userLogouttoUserDashboard() {
	window.localStorage.removeItem(localStorageRoomIdKey);
	window.localStorage.removeItem(localStorageRoomTemplateIdKey);
	window.localStorage.removeItem(localStorageGameSlugKey);
}

export function getToken() {
	return window.localStorage.getItem(localStorageTokenKey);
}
export function getRoomId() {
	return window.localStorage.getItem(localStorageRoomIdKey);
}
export function getUser() {
	const json = window.localStorage.getItem(localStorageDataKey);
	if (json) {
		return JSON.parse(json);
	}
	return null;
}

export function setUser(user: User) {
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(user));
}

export function setToken(token: string) {
	window.localStorage.setItem(localStorageTokenKey, token);
}

export function setRoomId(roomId: string) {
	window.localStorage.setItem(localStorageRoomIdKey, roomId);
}

export function setRoomTemplateId(roomTemplateId: string) {
	window.localStorage.setItem(localStorageRoomTemplateIdKey, roomTemplateId);
}

export function setOrganizationSlug(organizationSlug: string) {
	window.localStorage.setItem(localStorageOrgSlugKey, organizationSlug);
}

export function getRoomTemplateId() {
	return window.localStorage.getItem(localStorageRoomTemplateIdKey);
}
export function getOrganizationSlug() {
	return window.localStorage.getItem(localStorageOrgSlugKey);
}
export function getOrganizationStripeEnabled() {
	return window.localStorage.getItem(localStorageOrgStripeEnabled);
}
export function getGameSlug() {
	return window.localStorage.getItem(localStorageGameSlugKey);
}
export function getUserDashboardSlug() {
	return window.localStorage.getItem(localStorageUserDashboardKey);
}

export function updateBudyStatus(status: UserBuddyStatus) {
	const user = getUser();
	user.buddyStatus = status;

	// User is not a buddy if the status is rejected
	if (status === UserBuddyStatus.REJECTED) {
		user.isBuddy = false;
	}

	window.localStorage.setItem(localStorageDataKey, JSON.stringify(user));
}

export function updateUserProfile(
	profile: Partial<{ phoneNumber: string; birthDate: string; profilePicture: string }>,
) {
	const user = getUser();
	Object.assign(user, profile);
	window.localStorage.setItem(localStorageDataKey, JSON.stringify(user));
}

export function updateAdminUserProfile(profile: Partial<User>) {
	const user = getAdminUser();
	if (user) {
		Object.assign(user, profile);
		window.localStorage.setItem(localStorageAdminDataKey, JSON.stringify(user));
	}
}

export function setGameSlug(gameSlug: string) {
	if (!gameSlug) return;
	return window.localStorage.setItem(localStorageGameSlugKey, gameSlug);
}

export function getAdminUser() {
	const json = window.localStorage.getItem(localStorageAdminDataKey);
	if (json) {
		return JSON.parse(json);
	}
	return null;
}

export function getAdminToken() {
	return window.localStorage.getItem(localStorageAdminTokenKey);
}

export async function adminLogout() {
	const token = getAdminToken();
	window.localStorage.removeItem(localStorageAdminTokenKey);
	window.localStorage.removeItem(localStorageAdminDataKey);
	// Clear the pending verification flag on logout
	window.localStorage.removeItem(StorageKeys.ADMIN_PENDING_VERIFICATION);
	await apiClient("admin/logout", {
		data: {},
		token,
	});
	removeIdentity(); // Remove hubspotutk cookie
	window.setTimeout(() => {
		window.location.href = "/admin/login";
	}, 500);
}

export type AuthMeProfile = {
	id: string;
	email: string;
	username: string;
	role: string;
	organizationId: string;
	name: string;
};

export async function fetchAuthMe(bearerToken?: string | null): Promise<AuthMeProfile | null> {
	try {
		const response = await apiClient("auth/me", { getRawResponse: true, ...(bearerToken && { token: bearerToken }) });
		if (response.ok) {
			const json = await response.json();
			return json?.data ?? json ?? null;
		}
		return null;
	} catch {
		return null;
	}
}

export async function fetchSocketToken(bearerToken?: string | null): Promise<{ token: string } | null> {
	try {
		const response = await apiClient("auth/socket-token", { getRawResponse: true, ...(bearerToken && { token: bearerToken }) });
		if (response.ok) {
			const json = await response.json();
			const token = json?.data?.token ?? json?.token ?? null;
			if (token) return { token };
		}
		return null;
	} catch {
		return null;
	}
}

export async function sessionLogout(): Promise<void> {
	try {
		await apiClient("auth/logout", { data: {}, method: "POST" });
	} catch {
		// fire-and-forget — silently ignore errors
	}
}

export async function resetPassword(email: string, orgId?: string) {
	await apiClient("auth/reset-password", {
		data: { email, orgId },
	});
}

export async function resetPasswordConfirm(token: string, password: string) {
	await apiClient("auth/reset-password/confirm", {
		data: { token, password },
	});
}

export const setupPasswordConfirm = async (token: string, password: string) => {
	const response = await apiClient("auth/setup-password", {
		data: { token, password },
	});
	if (!response) return;
	return handleAdminLoginResponse(response as AdminLoginResponse);
};
